import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, ShieldCheck } from 'lucide-react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeTwoFactor, loginUser, openTwoFactor, verifyTwoFactorCode } from 'store/authSlice';
import { validateLoginIdentifier, validatePasswordField } from 'utils/validation';
import styles from './login.module.scss';
import { Alert } from 'components/alert/alert';
import TwoFactorModal from './TwoFactorModal';

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    isLoading,
    error: serverError,
    isAuthenticated,
    twoFactor,
    isTwoFactorOpen,
    twoFactorError,
    isVerifying,
  } = useAppSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    let isValid = true;
    const newErrors = { email: '', password: '' };

    const emailError = validateLoginIdentifier(formData.email);
    if (emailError) {
      newErrors.email = emailError;
      isValid = false;
    }

    const passwordError = validatePasswordField(formData.password);
    if (passwordError) {
      newErrors.password = passwordError;
      isValid = false;
    }

    setErrors(newErrors);

    if (isValid) {
      dispatch(
        loginUser({
          email: formData.email,
          password: formData.password,
        }),
      );
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginFormWrapper}>
        <div className={styles.logoContainer}>
          <h2 className={styles.logo}>
            <span className={styles.winLogo}>Dgenta System</span>
          </h2>
        </div>
        <form className={styles.loginForm} onSubmit={handleLogin}>
          <h1 className={styles.title}>Welcome</h1>
          <p className={styles.subtitle}>Please enter your details to sign in.</p>

          {serverError && <Alert message={serverError} type="error" />}

          <div className={styles.inputGroup}>
            <label htmlFor="email">Email or Username</label>
            <input
              type="text"
              id="email"
              className={`${styles.inputField} ${errors.email ? styles.inputError : ''}`}
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (errors.email) setErrors({ ...errors, email: '' });
              }}
              placeholder="Enter your email or username"
            />
            {errors.email && <span className={styles.errorMsg}>{errors.email}</span>}
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="password">Password</label>
            <div className={styles.inputWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className={`${styles.inputField} ${errors.password ? styles.inputError : ''}`}
                value={formData.password}
                onChange={(e) => {
                  setFormData({ ...formData, password: e.target.value });
                  if (errors.password) setErrors({ ...errors, password: '' });
                }}
                placeholder="••••••••"
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Toggle password visibility"
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm11-4.85L5.15 22.15 3.85 20.85 19.85 4.85 21.15 6.15z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <span className={styles.errorMsg}>{errors.password}</span>}
          </div>

          <button type="submit" className={styles.loginBtn} disabled={isLoading}>
            <LogIn size={18} />
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>

          {/* The challenge outlives a closed dialog, so an admin who dismissed
              it by accident gets back in without retyping their password. */}
          {twoFactor && !isTwoFactorOpen && (
            <button
              type="button"
              className={styles.resumeBtn}
              onClick={() => dispatch(openTwoFactor())}
            >
              <ShieldCheck size={16} />
              {twoFactor.mode === 'setup'
                ? 'Continue two-factor setup'
                : 'Enter authentication code'}
            </button>
          )}
        </form>
      </div>

      {twoFactor && (
        <TwoFactorModal
          open={isTwoFactorOpen}
          challenge={twoFactor}
          loading={isVerifying}
          error={twoFactorError}
          onClose={() => dispatch(closeTwoFactor())}
          onSubmit={(code) =>
            dispatch(verifyTwoFactorCode({ twoFactorToken: twoFactor.token, code }))
          }
        />
      )}
    </div>
  );
};

export default Login;
